import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatMonth, getPaymentTypeLabel, getMonthOptions } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Download,
  TrendingUp,
  TrendingDown,
  Calendar,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  FileText,
  Loader2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  ComposedChart,
  Area
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { MobileCardContainer, MobileCard, MobileCardHeader, MobileCardTitle, MobileCardContent, MobileCardRow } from '../components/MobileCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1A4D2E', '#E07A5F', '#2A9D8F', '#E9C46A'];

const periodOptions = [
  { value: 'all', label: 'Semua Periode' },
  { value: 'this_month', label: 'Bulan Ini' },
  { value: 'last_month', label: 'Bulan Kemarin' },
  { value: 'last_3_months', label: '3 Bulan Terakhir' },
  { value: 'last_6_months', label: '6 Bulan Terakhir' },
  { value: 'this_year', label: 'Tahun Ini' },
  { value: 'custom', label: 'Custom' },
];

const Laporan = () => {
  const [data, setData] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const chartRef = useRef(null);
  const profitChartRef = useRef(null);

  // Calculate date range based on filter
  const getDateRange = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    switch (periodFilter) {
      case 'all':
        return { start: null, end: null, label: 'Semua Periode' };
      case 'this_month':
        return {
          start: new Date(year, month, 1),
          end: new Date(year, month + 1, 0),
          label: 'Bulan Ini'
        };
      case 'last_month':
        return {
          start: new Date(year, month - 1, 1),
          end: new Date(year, month, 0),
          label: 'Bulan Kemarin'
        };
      case 'last_3_months':
        return {
          start: new Date(year, month - 2, 1),
          end: new Date(year, month + 1, 0),
          label: '3 Bulan Terakhir'
        };
      case 'last_6_months':
        return {
          start: new Date(year, month - 5, 1),
          end: new Date(year, month + 1, 0),
          label: '6 Bulan Terakhir'
        };
      case 'this_year':
        return {
          start: new Date(year, 0, 1),
          end: new Date(year, 11, 31),
          label: 'Tahun Ini'
        };
      case 'custom':
        return {
          start: customRange.start ? new Date(customRange.start) : null,
          end: customRange.end ? new Date(customRange.end) : null,
          label: 'Custom'
        };
      default:
        return { start: null, end: null, label: 'Semua Periode' };
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const range = getDateRange();
      
      let params = '';
      if (range.start && range.end) {
        const startDate = range.start.toISOString().split('T')[0];
        const endDate = range.end.toISOString().split('T')[0];
        params = `?start_date=${startDate}&end_date=${endDate}`;
      }
      
      const [reportRes, profitRes, expenseRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/income${params}`),
        axios.get(`${API_URL}/api/reports/profit${params}`),
        axios.get(`${API_URL}/api/reports/expense${params}`)
      ]);
      setData(reportRes.data);
      setProfitData(profitRes.data);
      setExpenseData(expenseRes.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Gagal memuat laporan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter, customRange]);

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
    if (value === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
    }
  };

  const pieData = data?.by_type ? Object.entries(data.by_type).map(([key, value]) => ({
    name: getPaymentTypeLabel(key),
    value
  })) : [];

  const expensePieData = expenseData?.by_category ? Object.entries(expenseData.by_category)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value
    })) : [];

  const chartData = data?.monthly_data?.map(item => ({
    month: formatMonth(item.month).split(' ')[0],
    Sewa: item.sewa,
    Listrik: item.listrik,
    Air: item.air,
    Lainnya: item.lainnya
  })) || [];

  const handleExport = () => {
    if (!data?.monthly_data?.length) {
      toast.error('Tidak ada data untuk diekspor');
      return;
    }

    const headers = ['Bulan', 'Sewa', 'Listrik', 'Air', 'Lainnya', 'Total'];
    const rows = data.monthly_data.map(item => [
      formatMonth(item.month),
      item.sewa,
      item.listrik,
      item.air,
      item.lainnya,
      item.total
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laporan-keuangan-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Laporan berhasil diunduh');
  };

  const handleExportPdf = async () => {
    if (!data?.monthly_data?.length && !profitData?.monthly_data?.length) {
      toast.error('Tidak ada data untuk diekspor');
      return;
    }

    setExportingPdf(true);
    toast.info('Sedang membuat PDF...');

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Header
      pdf.setFillColor(26, 77, 46); // Primary color
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('KOS CEMARA', margin, 15);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Laporan Keuangan Bulanan', margin, 23);
      
      const today = new Date().toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
      pdf.setFontSize(10);
      pdf.text(`Dicetak: ${today}`, margin, 30);

      // Period info
      const range = getDateRange();
      let periodText = range.label || 'Semua Periode';
      if (range.start && range.end) {
        const startStr = range.start.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
        const endStr = range.end.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
        periodText = `${startStr} - ${endStr}`;
      } else if (range.start) {
        const startStr = range.start.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
        periodText = `Mulai ${startStr}`;
      } else if (range.end) {
        const endStr = range.end.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
        periodText = `Sampai ${endStr}`;
      }
      pdf.text(`Periode: ${periodText}`, pageWidth - margin - 50, 30);

      yPos = 45;

      // Summary Section
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Ringkasan Keuangan', margin, yPos);
      yPos += 8;

      // Summary boxes
      const summaryData = [
        { label: 'Total Pendapatan', value: formatCurrency(profitData?.summary?.total_income || data?.total_income || 0), color: [34, 197, 94] },
        { label: 'Total Pengeluaran', value: formatCurrency(profitData?.summary?.total_expense || 0), color: [239, 68, 68] },
        { label: 'Total Profit', value: formatCurrency(profitData?.summary?.total_profit || 0), color: [59, 130, 246] },
        { label: 'Rata-rata Margin', value: `${profitData?.summary?.avg_margin_percent || 0}%`, color: [168, 85, 247] }
      ];

      const boxWidth = (pageWidth - margin * 2 - 15) / 4;
      summaryData.forEach((item, idx) => {
        const xPos = margin + (boxWidth + 5) * idx;
        pdf.setFillColor(...item.color);
        pdf.roundedRect(xPos, yPos, boxWidth, 20, 2, 2, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(item.label, xPos + 3, yPos + 6);
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(item.value, xPos + 3, yPos + 14);
      });

      yPos += 30;

      // Profit Margin Chart as Image
      if (profitChartRef.current && profitData?.monthly_data?.length > 0) {
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Grafik Pendapatan vs Pengeluaran', margin, yPos);
        yPos += 5;

        try {
          const canvas = await html2canvas(profitChartRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - margin * 2;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          if (yPos + imgHeight > pageHeight - 20) {
            pdf.addPage();
            yPos = margin;
          }
          
          pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, Math.min(imgHeight, 80));
          yPos += Math.min(imgHeight, 80) + 10;
        } catch (err) {
          console.error('Error capturing chart:', err);
        }
      }

      // Profit Margin Table
      if (profitData?.monthly_data?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Detail Profit Bulanan', margin, yPos);
        yPos += 5;

        autoTable(pdf, {
          startY: yPos,
          head: [['Bulan', 'Pendapatan', 'Pengeluaran', 'Profit', 'Margin']],
          body: profitData.monthly_data.map(item => [
            item.month_name,
            formatCurrency(item.income),
            formatCurrency(item.expense),
            formatCurrency(item.profit),
            `${item.margin_percent}%`
          ]),
          theme: 'striped',
          headStyles: { fillColor: [26, 77, 46], textColor: 255 },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' }
          },
          margin: { left: margin, right: margin }
        });

        yPos = pdf.lastAutoTable.finalY + 10;
      }

      // Income by Type Table
      if (data?.monthly_data?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Detail Pendapatan per Bulan', margin, yPos);
        yPos += 5;

        const tableData = data.monthly_data.map(item => [
          formatMonth(item.month),
          formatCurrency(item.sewa),
          formatCurrency(item.listrik),
          formatCurrency(item.air),
          formatCurrency(item.lainnya),
          formatCurrency(item.total)
        ]);

        // Add total row
        tableData.push([
          'TOTAL',
          formatCurrency(data.by_type?.sewa || 0),
          formatCurrency(data.by_type?.listrik || 0),
          formatCurrency(data.by_type?.air || 0),
          formatCurrency(data.by_type?.lainnya || 0),
          formatCurrency(data.total_income || 0)
        ]);

        autoTable(pdf, {
          startY: yPos,
          head: [['Bulan', 'Sewa', 'Listrik', 'Air', 'Lainnya', 'Total']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [26, 77, 46], textColor: 255 },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
            5: { halign: 'right', fontStyle: 'bold' }
          },
          margin: { left: margin, right: margin },
          didParseCell: function(data) {
            // Make last row bold (total)
            if (data.row.index === tableData.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [240, 240, 240];
            }
          }
        });
      }

      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Halaman ${i} dari ${totalPages} | Kos Cemara - Laporan Keuangan`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const filename = `Laporan-Keuangan-KosCemara-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
      toast.success('Laporan PDF berhasil diunduh');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Gagal membuat PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="laporan-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Laporan Keuangan</h1>
          <p className="text-muted-foreground text-sm mt-1">Analisis pendapatan dan pengeluaran kost Anda</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" size="sm" className="rounded-xl" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button 
            onClick={handleExportPdf} 
            size="sm"
            className="rounded-xl" 
            disabled={exportingPdf}
            data-testid="export-pdf-btn"
          >
            {exportingPdf ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-1" />
            )}
            PDF
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={periodFilter} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-[160px] sm:w-[180px]" data-testid="period-filter">
            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Pilih Periode" />
          </SelectTrigger>
          <SelectContent>
            {periodOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {periodFilter === 'custom' && (
          <Popover open={showCustomPicker} onOpenChange={setShowCustomPicker}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                {customRange.start && customRange.end 
                  ? `${customRange.start} - ${customRange.end}`
                  : 'Pilih Tanggal'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tanggal Mulai</Label>
                  <Input
                    type="date"
                    value={customRange.start}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tanggal Akhir</Label>
                  <Input
                    type="date"
                    value={customRange.end}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
                <Button onClick={() => { setShowCustomPicker(false); fetchData(); }} className="w-full">
                  Terapkan
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        
        {periodFilter !== 'all' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPeriodFilter('all')}
          >
            Reset
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-24 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  {formatCurrency(data?.total_income || 0)}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Total Pendapatan
                </p>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-24 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className="text-xl md:text-2xl font-bold text-red-600">
                  {formatCurrency(expenseData?.total_expense || 0)}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Total Pengeluaran
                </p>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-24 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className={`text-xl md:text-2xl font-bold ${((data?.total_income || 0) - (expenseData?.total_expense || 0)) >= 0 ? 'text-primary' : 'text-red-600'}`}>
                  {formatCurrency((data?.total_income || 0) - (expenseData?.total_expense || 0))}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Profit Bersih
                </p>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-16 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className="text-xl md:text-2xl font-bold">
                  {data?.payment_count || 0} / {expenseData?.expense_count || 0}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Transaksi (In/Out)
                </p>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Profit Margin Section */}
      {profitData && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Analisis Profit Margin
          </h2>
          
          {/* Profit Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Total Pendapatan</p>
                  <ArrowUpRight className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-base md:text-xl font-bold text-green-600">
                  {formatCurrency(profitData.summary?.total_income || 0)}
                </p>
              </div>
            </Card>
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Total Pengeluaran</p>
                  <ArrowDownRight className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-base md:text-xl font-bold text-red-600">
                  {formatCurrency(profitData.summary?.total_expense || 0)}
                </p>
              </div>
            </Card>
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Total Profit</p>
                  <Wallet className="w-4 h-4 text-primary" />
                </div>
                <p className={`text-base md:text-xl font-bold ${(profitData.summary?.total_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(profitData.summary?.total_profit || 0)}
                </p>
              </div>
            </Card>
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Rata-rata Margin</p>
                  <Percent className="w-4 h-4 text-primary" />
                </div>
                <p className={`text-base md:text-xl font-bold ${(profitData.summary?.avg_margin_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {profitData.summary?.avg_margin_percent || 0}%
                </p>
              </div>
            </Card>
          </div>

          {/* Profit Margin Chart */}
          <Card className="p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg">Pendapatan vs Pengeluaran (6 Bulan Terakhir)</CardTitle>
              <CardDescription>Perbandingan pendapatan dan biaya operasional bulanan</CardDescription>
            </CardHeader>
            <CardContent className="p-0 h-[350px]" ref={profitChartRef}>
              {profitData.monthly_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={profitData.monthly_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month_name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickFormatter={(value) => `${(value / 1000000).toFixed(0)}jt`}
                    />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name === 'income' ? 'Pendapatan' : name === 'expense' ? 'Pengeluaran' : 'Profit']}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.75rem'
                      }}
                    />
                    <Legend formatter={(value) => value === 'income' ? 'Pendapatan' : value === 'expense' ? 'Pengeluaran' : 'Profit'} />
                    <Bar dataKey="income" fill="#22c55e" name="income" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" fill="#ef4444" name="expense" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={3} name="profit" dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Belum ada data profit margin
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Profit Table */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Detail Profit Bulanan</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Mobile Cards View */}
              <MobileCardContainer className="mb-0">
                {profitData.monthly_data?.map((item) => (
                  <MobileCard key={item.month} className="bg-muted/30">
                    <MobileCardHeader>
                      <MobileCardTitle className="font-semibold">{item.month_name}</MobileCardTitle>
                      <Badge variant={item.margin_percent >= 50 ? 'default' : item.margin_percent >= 0 ? 'secondary' : 'destructive'}>
                        {item.margin_percent}%
                      </Badge>
                    </MobileCardHeader>
                    <MobileCardContent>
                      <MobileCardRow label="Pendapatan" value={formatCurrency(item.income)} valueClassName="text-green-600" />
                      <MobileCardRow label="Pengeluaran" value={formatCurrency(item.expense)} valueClassName="text-red-600" />
                      <MobileCardRow label="Profit" value={formatCurrency(item.profit)} valueClassName={item.profit >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'} />
                    </MobileCardContent>
                  </MobileCard>
                ))}
              </MobileCardContainer>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bulan</TableHead>
                      <TableHead className="text-right">Pendapatan</TableHead>
                      <TableHead className="text-right">Pengeluaran</TableHead>
                      <TableHead className="text-right">Profit</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profitData.monthly_data?.map((item) => (
                      <TableRow key={item.month}>
                        <TableCell className="font-medium">{item.month_name}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(item.income)}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(item.expense)}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(item.profit)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={item.margin_percent >= 50 ? 'default' : item.margin_percent >= 0 ? 'secondary' : 'destructive'}>
                            {item.margin_percent}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart */}
        <Card className="lg:col-span-2 p-6">
          <CardHeader className="p-0 pb-6">
            <CardTitle className="font-heading text-lg">Pendapatan per Bulan</CardTitle>
          </CardHeader>
          <CardContent className="p-0 h-[350px]" ref={chartRef}>
            {loading ? (
              <Skeleton className="w-full h-full" />
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={12}
                    tickFormatter={(value) => `${(value / 1000000).toFixed(0)}jt`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.75rem'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Sewa" stackId="a" fill={COLORS[0]} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Listrik" stackId="a" fill={COLORS[1]} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Air" stackId="a" fill={COLORS[2]} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Lainnya" stackId="a" fill={COLORS[3]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Belum ada data untuk ditampilkan
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="p-6">
          <CardHeader className="p-0 pb-6">
            <CardTitle className="font-heading text-lg">Komposisi Pendapatan</CardTitle>
          </CardHeader>
          <CardContent className="p-0 h-[350px]">
            {loading ? (
              <Skeleton className="w-full h-full" />
            ) : pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Belum ada data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expense Section */}
      {expenseData && expenseData.total_expense > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Expense Pie Chart */}
          <Card className="p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg text-red-600">Komposisi Pengeluaran</CardTitle>
            </CardHeader>
            <CardContent className="p-0 h-[300px]">
              {expensePieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expensePieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {expensePieData.map((entry, index) => (
                        <Cell key={`expense-cell-${index}`} fill={['#E07A5F', '#F4A261', '#E76F51', '#264653', '#2A9D8F', '#E9C46A'][index % 6]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Belum ada pengeluaran
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expense Detail by Category */}
          <Card className="lg:col-span-2 p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg">Detail Pengeluaran per Kategori</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-3">
                {expenseData.by_category && Object.entries(expenseData.by_category)
                  .filter(([_, value]) => value > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, amount], index) => (
                    <div key={category} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: ['#E07A5F', '#F4A261', '#E76F51', '#264653', '#2A9D8F', '#E9C46A'][index % 6] }}
                        />
                        <span className="font-medium capitalize">{category}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-red-600">{formatCurrency(amount)}</p>
                        <p className="text-xs text-muted-foreground">
                          {((amount / expenseData.total_expense) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ))}
                {(!expenseData.by_category || Object.values(expenseData.by_category).every(v => v === 0)) && (
                  <div className="text-center py-8 text-muted-foreground">
                    Belum ada data pengeluaran
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detail Table */}
      <Card className="overflow-hidden">
        <CardHeader className="p-6 pb-4">
          <CardTitle className="font-heading text-lg">Detail per Bulan</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data?.monthly_data?.length > 0 ? (
            <>
              {/* Mobile Cards View */}
              <div className="md:hidden p-4 space-y-3">
                {data.monthly_data.map((item) => (
                  <MobileCard key={item.month} className="bg-muted/30">
                    <MobileCardHeader>
                      <MobileCardTitle className="font-semibold">{formatMonth(item.month)}</MobileCardTitle>
                      <span className="text-lg font-bold text-primary">{formatCurrency(item.total)}</span>
                    </MobileCardHeader>
                    <MobileCardContent>
                      <MobileCardRow label="Sewa" value={formatCurrency(item.sewa)} />
                      <MobileCardRow label="Listrik" value={formatCurrency(item.listrik)} />
                      <MobileCardRow label="Air" value={formatCurrency(item.air)} />
                      <MobileCardRow label="Lainnya" value={formatCurrency(item.lainnya)} />
                    </MobileCardContent>
                  </MobileCard>
                ))}
                {/* Total Card */}
                <MobileCard className="bg-primary/10 border-primary/30">
                  <MobileCardHeader>
                    <MobileCardTitle className="font-bold">Total Keseluruhan</MobileCardTitle>
                    <span className="text-lg font-bold text-primary">{formatCurrency(data.total_income)}</span>
                  </MobileCardHeader>
                  <MobileCardContent>
                    <MobileCardRow label="Sewa" value={formatCurrency(data.by_type?.sewa || 0)} />
                    <MobileCardRow label="Listrik" value={formatCurrency(data.by_type?.listrik || 0)} />
                    <MobileCardRow label="Air" value={formatCurrency(data.by_type?.air || 0)} />
                    <MobileCardRow label="Lainnya" value={formatCurrency(data.by_type?.lainnya || 0)} />
                  </MobileCardContent>
                </MobileCard>
              </div>

              {/* Desktop Table View */}
              <Table className="data-table hidden md:table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Bulan</TableHead>
                    <TableHead className="text-right">Sewa</TableHead>
                    <TableHead className="text-right">Listrik</TableHead>
                    <TableHead className="text-right">Air</TableHead>
                    <TableHead className="text-right">Lainnya</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.monthly_data.map((item) => (
                    <TableRow key={item.month}>
                      <TableCell className="font-medium">{formatMonth(item.month)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.sewa)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.listrik)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.air)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.lainnya)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(item.total)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.sewa || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.listrik || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.air || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.lainnya || 0)}</TableCell>
                    <TableCell className="text-right text-primary">{formatCurrency(data.total_income)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              Belum ada data untuk ditampilkan
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Laporan;
